import React, { ReactElement, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { View, LayoutAnimation } from 'react-native';
import { useOwnProfile } from '_api/useProfile';
import { dismissAddProfile, dismissModal, setShowParkingFeedbackModalAfter } from '_redux/modals';
import { showToast, useAppDispatch, useGlobalState } from '_utils';
import { track } from '_utils/Amplitude';
import { getFullName } from '_utils/misc';
import { captureUserFeedback } from '_utils/Sentry';
import { screenMargin, smallestMargin, smallestMarginPixels, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CheckBox, HeimeText, IconButton, SecondaryButton, ControlledInput } from 'Components';
import DividedImageModal from './components/DividedImageModal';

const ParkingSharingFeedbackModal = (): ReactElement | null => {
    const { showParkingFeedbackModalAfter, showModalAfter } = useGlobalState((state) => state.addProfileModal);

    const shouldShow =
        showParkingFeedbackModalAfter &&
        moment.unix(showParkingFeedbackModalAfter).isBefore(moment()) &&
        (!showModalAfter || moment.unix(showModalAfter).isBefore(moment()));

    if (shouldShow) {
        return <ActualModal />;
    }
    return null;
};

const ActualModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [neverAsk, setIsNeverAsk] = useState(false);
    const { theme } = useThemeContext();

    const [feedbackState, setFeedbackState] = useState<false | 'positive' | 'negative'>(false);
    const [feedbackText, setFeedbackText] = useState<string | undefined>('');
    const [allowContact, setAllowContact] = useState(true);
    const { data: ownProfile } = useOwnProfile();

    const handleFeedback = (isPositive: boolean) => {
        const feedback = isPositive ? 'positive' : 'negative';
        track('Feedback received parking sharing', { feedback });

        setFeedbackState(feedback);
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

        showToast({
            type: 'success',
            header: t('rateUs:parkingSharingFeedbackModal:feedbackReceivedHeader'),
            text: t('rateUs:parkingSharingFeedbackModal:feedbackReceivedText'),
            position: 'bottom',
        });
    };

    const handleActionInModal = () => {
        dispatch(dismissModal());
    };

    const handleSubmitText = () => {
        if (feedbackText?.trim()) {
            captureUserFeedback(
                'parkingSharingFeedback',
                `location:${location}, feedback: ${feedbackState}, text: ${feedbackText}`,
                getFullName(ownProfile?.fname, ownProfile?.lname),
                allowContact,
            );
        }

        dispatch(setShowParkingFeedbackModalAfter(moment().add({ years: 2 }).unix()));
        handleActionInModal();
    };

    const handleAllowContact = () => {
        setAllowContact(!allowContact);
    };

    const containerStyle = {
        padding: screenMargin,
        paddingBottom: screenMargin - smallestMarginPixels,
        marginTop: smallestMargin,
    };

    useEffect(() => {
        track('Give parking sharing feedback modal shown');
    }, []);

    const toggleNeverAsk = () => {
        setIsNeverAsk(!neverAsk);
    };

    const handleDismiss = (dontAskAgain = neverAsk) => {
        track('Parking sharing feedback modal dismissed', { dontAskAgain });
        if (dontAskAgain || feedbackState) {
            // Dont ask for two years
            dispatch(dismissAddProfile(moment().add({ years: 2 }).unix()));
        } else {
            dispatch(dismissAddProfile(moment().add({ day: 1 }).unix()));
            // Ask again in a week
        }
        handleActionInModal();
    };

    return (
        <DividedImageModal
            onDismiss={() => handleDismiss()}
            top={<HeimeText variant="title">{t('rateUs:parkingSharingFeedbackModal:title')}</HeimeText>}
            bottom={
                <>
                    <View style={containerStyle}>
                        <HeimeText variant="title" style={{ fontSize: subtitleFontSize }}>
                            {feedbackState === false
                                ? t('rateUs:parkingSharingFeedbackModal:title_description')
                                : feedbackState === 'positive'
                                  ? t('rateUs:parkingSharingFeedbackModal:layoutQuestionPositive')
                                  : t('rateUs:parkingSharingFeedbackModal:layoutQuestionNegative')}
                        </HeimeText>
                        {!feedbackState ? (
                            <View>
                                <View
                                    style={{
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        gap: screenMargin,
                                        marginTop: screenMargin,
                                    }}
                                >
                                    <IconButton
                                        icon="thumbsUp"
                                        onPress={() => handleFeedback(true)}
                                        accessibilityHint={t('rateUs:parkingSharingFeedbackModal:thumbsUp')}
                                    />
                                    <IconButton
                                        icon="thumbsDown"
                                        onPress={() => handleFeedback(false)}
                                        accessibilityHint={t('rateUs:parkingSharingFeedbackModal:thumbsDown')}
                                    />
                                </View>

                                <View>
                                    <SecondaryButton
                                        text={t('rateUs:parkingSharingFeedbackModal:back')}
                                        onPress={handleDismiss}
                                        style={{ marginTop: screenMargin * 2 }}
                                    />

                                    <CheckBox
                                        title={t('profile:updateInfoModal:checkbox')}
                                        onPress={toggleNeverAsk}
                                        checked={neverAsk}
                                    />
                                </View>
                            </View>
                        ) : (
                            <>
                                <View>
                                    <ControlledInput
                                        value={feedbackText}
                                        onChange={setFeedbackText}
                                        placeholder={t('rateUs:parkingSharingFeedbackModal:textPlaceholder')}
                                        multiline
                                        numberOfLines={3}
                                        containerStyle={{ marginTop: smallestMargin }}
                                        style={{ backgroundColor: theme.mainBackground }}
                                    />
                                    <CheckBox
                                        title={t('rateUs:parkingSharingFeedbackModal:allowContact')}
                                        onPress={handleAllowContact}
                                        checked={allowContact}
                                    />
                                </View>
                                <IconButton
                                    text={t('rateUs:parkingSharingFeedbackModal:submit')}
                                    icon="send"
                                    onPress={handleSubmitText}
                                    style={{ alignSelf: 'flex-end', marginTop: screenMargin }}
                                />
                            </>
                        )}
                    </View>
                </>
            }
        />
    );
};

export default ParkingSharingFeedbackModal;
